/**
 * Resolution returned by {@link resolveDropdownOptionTooltip}: the element the
 * native `title` must be written onto, and the full option text to show.
 */
export interface DropdownOptionTooltip {
  /**
   * Element the `title` is written onto — ALWAYS the option's own `<li>`, never
   * an inner span. In an `ejs-dropdowntree` the element actually under the pointer
   * is the absolutely-positioned `.e-fullrow` overlay, because Syncfusion sets
   * `pointer-events: none` on the `.e-text-content` that holds the label (see
   * `.e-treeview.e-fullrow-wrap .e-text-content` in the EJ2 treeview theme). The
   * browser resolves a tooltip by walking ANCESTORS of the hovered element, and
   * `.e-fullrow` and `.e-text-content` are siblings — so only the `<li>` above
   * both of them reaches every hover target. A parent `<li>` also wraps its
   * children, but each child carries its own `title` and the nearest titled
   * ancestor wins, so a child row never shows its parent's label.
   */
  anchor: HTMLElement;
  /** Complete, untruncated option label. */
  text: string;
}

/**
 * Attribute stamped on an anchor whose `title` WE wrote. Lets a later pass tell
 * "our" title apart from one Syncfusion (or a call-site `itemTemplate`) already
 * put there, so an existing, more specific tooltip is never clobbered.
 */
export const DROPDOWN_OPTION_TOOLTIP_MARKER = 'data-option-tooltip';

/**
 * Every EJ2 popup-list row. Covers `ejs-dropdownlist`, `ejs-multiselect`,
 * `ejs-combobox`, `ejs-autocomplete` (all render `DropDownBase` items) and the
 * `ejs-dropdowntree`'s inner TreeView — they all use `.e-list-item`.
 * `.e-list-group-item` is the group header rendered when `fields.groupBy` is set.
 */
const OPTION_SELECTOR = '.e-list-item,.e-list-group-item';

/** Wrapper `<div>` holding a TreeView node's own row (`ejs-dropdowntree` only). */
const TREE_ROW_CLASS = 'e-text-content';

/** Span holding the node's label inside {@link TREE_ROW_CLASS}. */
const TREE_LABEL_SELECTOR = '.e-list-text';

/**
 * Decide whether a hovered element sits inside a dropdown option and, if so,
 * what its complete label is.
 *
 * Native `title` is used deliberately (over `ejs-tooltip`): the popup is
 * portalled to `body`, and this whole app renders under a global `body { zoom }`
 * (`--ui-scale`, see `styles.scss`) that drifts an EJ2 tooltip away from its
 * target unless every anchor gets the counter-zoom `cssClass` treatment. The
 * browser's own tooltip is immune, and it is the same reveal the grids already
 * use for clipped cells (`grid-tooltip.util.ts`).
 *
 * The text is returned in full, not only when clipped: a popup can still be
 * animating to its final width on the first hover, so an overflow measurement
 * taken there is unreliable, and the requirement is simply "show the complete
 * name on mouseover".
 *
 * `from` short-circuits the pointer moving WITHIN one option row. `mouseover` fires again on
 * every internal boundary crossed (`<li>` → its `.e-checkbox-wrapper` → its `.e-list-text`, or
 * a tree row's `.e-fullrow` overlay → its `.e-text-content` sibling), and each of those repeats
 * would re-read the label and rewrite the identical `title`. The row was already resolved when
 * the pointer entered it, so the repeats are dropped.
 *
 * That test compares the two ends' OWN option rows; it deliberately does NOT ask whether the
 * row `contains` the previous element. Inside an `ejs-dropdowntree` an option's `<li>` also
 * contains every DESCENDANT option's `<li>`, so a `contains` test would swallow the parent→child
 * move and leave a child row that was entered from its parent with no tooltip at all.
 *
 * It is checked only AFTER `target`'s own row is found, so the overwhelmingly common event —
 * a hover anywhere else in the app, which this document-level listener also receives — still
 * costs exactly one `closest()` and no second walk.
 *
 * @param target The element under the pointer (a `mouseover` event target).
 * @param from The element the pointer came from (`MouseEvent.relatedTarget`), when known.
 * @returns The anchor + full text, or `null` when the pointer is not over an option (or has
 *   not left the option it was already on).
 */
export function resolveDropdownOptionTooltip(
  target: Element,
  from: EventTarget | null = null,
): DropdownOptionTooltip | null {
  const item = target.closest<HTMLElement>(OPTION_SELECTOR);
  if (!item) {
    return null;
  }
  if (from instanceof Element && from.closest<HTMLElement>(OPTION_SELECTOR) === item) {
    return null;
  }
  const text = readOwnLabel(item);
  return text ? { anchor: item, text } : null;
}

/**
 * The complete label of THIS row, excluding any nested child rows.
 *
 * A flat list item's own `textContent` is already exactly the label — a
 * CheckBox-mode row also holds a `.e-checkbox-wrapper`, but that contributes no
 * text nodes (its tick is a CSS icon). A TreeView node, by contrast, nests its
 * children's `<ul>` inside the same `<li>`, so reading the whole `textContent`
 * would concatenate every descendant's label into one unreadable tooltip; its
 * own label is read from the `.e-list-text` inside its own `.e-text-content` row.
 *
 * That row is found by walking direct children rather than with a `:scope >`
 * query. Syncfusion stamps generated ids like `f087-0` on these rows, and
 * `:scope` resolution throws `SyntaxError: '…,,,ee8-0.e-list-item > …' is not a
 * valid selector` on such an id — silently costing every option its tooltip.
 */
function readOwnLabel(item: HTMLElement): string {
  for (const child of Array.from(item.children)) {
    if (child.classList.contains(TREE_ROW_CLASS)) {
      const label = child.querySelector<HTMLElement>(TREE_LABEL_SELECTOR);
      if (label) {
        return (label.textContent ?? '').trim();
      }
    }
  }
  return (item.textContent ?? '').trim();
}

/**
 * Write the resolved full label onto the anchor's `title`, unless the anchor
 * already carries a title somebody else set. Re-writes our own stale title when
 * the row is recycled with new text (Syncfusion reuses `<li>` elements while
 * filtering), and is a no-op when the title already matches.
 */
export function applyDropdownOptionTooltip({ anchor, text }: DropdownOptionTooltip): void {
  const current = anchor.getAttribute('title');
  const isOurs = anchor.hasAttribute(DROPDOWN_OPTION_TOOLTIP_MARKER);
  if (current && !isOurs) {
    return;
  }
  if (current === text) {
    return;
  }
  anchor.setAttribute('title', text);
  anchor.setAttribute(DROPDOWN_OPTION_TOOLTIP_MARKER, '');
}
